import type { Player } from "../types.js";
import {
  allocateStrokes,
  courseHandicap,
  effectiveAllowancePercent,
  holesInPlay,
  playingHandicap,
} from "../handicap.js";
import type { Format, FormatScoreInput, RoundResult } from "./types.js";
import {
  allowanceNote,
  buildScoreMap,
  describeSegmentPoints,
  holeCount,
  holeNumbersInPlay,
  scoreSegmentsByTotal,
  teamGrossOnHole,
} from "./util.js";

function teamKey(team: string[]): string {
  return [...team].sort().join(",");
}

/**
 * The pair's one ball, net of their averaged handicap. Shared by `score` and the side
 * breakdown so the grid can't disagree with the points about what a side shot.
 */
function teamValue({ round, course, players, scores }: FormatScoreInput) {
  const scoreMap = buildScoreMap(scores);
  const playerById = new Map(players.map((p) => [p.id, p]));
  const holes = holesInPlay(course.holes, round.holeSet);
  const allowance = effectiveAllowancePercent(round.handicapAllowance, holes.length);

  const strokesCache = new Map<string, Map<number, number>>();
  const strokesForTeam = (team: string[]): Map<number, number> => {
    const key = teamKey(team);
    const cached = strokesCache.get(key);
    if (cached) return cached;

    const chs = team
      .map((id) => playerById.get(id))
      .filter((p): p is Player => !!p)
      .map((p) => courseHandicap(p.handicapIndex, course.slope));
    const avgCh = chs.reduce((sum, v) => sum + v, 0) / (chs.length || 1);
    const teamPlayingHcp = playingHandicap(avgCh, allowance);

    const strokes = allocateStrokes(teamPlayingHcp, holes);
    strokesCache.set(key, strokes);
    return strokes;
  };

  return (team: string[], hole: number): number | null => {
    const gross = teamGrossOnHole(scoreMap, team, hole);
    if (gross == null) return null;
    const received = strokesForTeam(team).get(hole) ?? 0;
    return gross - received;
  };
}

export const alternateShot: Format = {
  id: "alternateShot",
  name: "Alternate Shot / Foursomes",
  scope: "team",
  uses: { teamPoints: true, handicapAllowance: true },
  entry: "perTeam",
  defaults: { handicapAllowance: 100 },
  configSchema: { type: "object", properties: {} },
  perHoleSchema: null,

  score(input): RoundResult {
    const { round, course, players, onlyHoles } = input;
    return scoreSegmentsByTotal(
      round,
      players,
      holeNumbersInPlay(round, course),
      teamValue(input),
      onlyHoles,
    );
  },

  sideValueOnHole: teamValue,

  describeRules(round, _players, course) {
    return [
      `Alternate shot / foursomes across ${holeCount(round, course)} holes: one ball per team, players alternate shots. One score per team per hole.`,
      `Team handicap: average of the two players' course handicaps, at ${round.handicapAllowance}% allowance${allowanceNote(round, course)}.`,
      describeSegmentPoints(round),
    ];
  },
};
